import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Stack, Button, IconButton, Tooltip, DialogTitle, DialogContent, DialogActions, Divider,
  Alert, CircularProgress, TextField, Typography,
} from '@mui/material';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Dialog from '../ManagedModal';

/**
 * HuongDanFlow — hộp "Hướng dẫn sử dụng" của trình soạn Flow: hiển thị tài liệu flow-guide.md của máy chủ
 * (`<server_url>/docs/flow-guide.md`, file tĩnh — không cần đăng nhập), cùng cách làm với nút "Hướng dẫn" của
 * UserModuleManager: render Markdown, nút "Copy Markdown" (dán cho AI/đồng nghiệp), mở bản gốc ở tab mới.
 * Thêm MỤC LỤC theo các tiêu đề cấp 2 và ô lọc mục vì tài liệu dài (hơn 1.000 dòng, 12 mục + danh mục node).
 *
 * Nội dung chỉ tải khi mở lần đầu và được nhớ trong phiên (biến cấp module), mở lại không tải lại.
 */

let _banNho = null;   // { url, text } — nội dung đã tải trong phiên

// Copy an toàn: ưu tiên Clipboard API, lùi về execCommand khi trang không chạy trong ngữ cảnh bảo mật.
async function copyText(text) {
  try {
    if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* rơi xuống cách dự phòng */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

// Mã neo cho tiêu đề (bỏ dấu, chữ thường, gạch nối) — dùng cho mục lục.
const neo = (s) => 'fg-' + String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const chuCua = (children) => React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : (c?.props?.children ? chuCua(c.props.children) : ''))).join('');

// Cùng phong cách vùng Markdown với UserModuleManager.
const mdSx = {
  '& h1, & h2, & h3': { mt: 2.5, mb: 1, fontWeight: 700, lineHeight: 1.25, scrollMarginTop: 8 },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.25rem', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 },
  '& h3': { fontSize: '1.05rem' },
  '& p': { my: 1, lineHeight: 1.6 },
  '& ul, & ol': { pl: 3, my: 1 },
  '& li': { my: 0.25 },
  '& a': { color: 'primary.main' },
  '& blockquote': { m: 0, my: 1.5, pl: 2, borderLeft: '4px solid', borderColor: 'divider', color: 'text.secondary' },
  '& code': { fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'action.hover', px: 0.5, py: 0.1, borderRadius: 0.5 },
  '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5, display: 'block', overflowX: 'auto' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
  '& th': { bgcolor: 'action.hover', fontWeight: 700 },
  '& img': { maxWidth: '100%' },
  '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 2 },
};

const HuongDanFlow = ({ open, onClose, apiUrl }) => {
  const url = `${String(apiUrl || '').replace(/\/+$/, '')}/docs/flow-guide.md`;
  const [text, setText] = useState(_banNho && _banNho.url === url ? _banNho.text : '');
  const [dangTai, setDangTai] = useState(false);
  const [loi, setLoi] = useState('');
  const [daCopy, setDaCopy] = useState(false);
  const [loc, setLoc] = useState('');
  const vungNoiDung = useRef(null);

  useEffect(() => {
    if (!open || text) return undefined;
    let huy = false;
    setDangTai(true); setLoi('');
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then((t) => { if (huy) return; _banNho = { url, text: t }; setText(t); })
      .catch((e) => { if (!huy) setLoi(e.message || String(e)); })
      .finally(() => { if (!huy) setDangTai(false); });
    return () => { huy = true; };
  }, [open, url, text]);

  // Mục lục: các tiêu đề cấp 2 (## …) — bỏ qua dòng trong khối code.
  const mucLuc = useMemo(() => {
    const ra = []; let trongCode = false;
    for (const d of String(text).split('\n')) {
      if (/^\s*```/.test(d)) { trongCode = !trongCode; continue; }
      const m = !trongCode && /^##\s+(.+?)\s*$/.exec(d);
      if (m) ra.push({ ten: m[1].replace(/[`*]/g, ''), id: neo(m[1].replace(/[`*]/g, '')) });
    }
    return ra;
  }, [text]);
  const mucLoc = useMemo(() => {
    const q = neo(loc).replace(/^fg-/, '');
    return q ? mucLuc.filter((x) => x.id.includes(q)) : mucLuc;
  }, [mucLuc, loc]);

  const denMuc = (id) => {
    const el = vungNoiDung.current && vungNoiDung.current.querySelector('#' + CSS.escape(id));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const onCopy = async () => {
    if (!text) return;
    if (await copyText(text)) { setDaCopy(true); setTimeout(() => setDaCopy(false), 1500); }
  };
  const tieuDe = (Tag) => ({ children }) => <Tag id={neo(chuCua(children))}>{children}</Tag>;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MenuBookRoundedIcon color="primary" />
            <span>Hướng dẫn xây dựng Flow</span>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Button size="small" variant="outlined" color={daCopy ? 'success' : 'primary'}
              startIcon={daCopy ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />} onClick={onCopy} disabled={!text}>
              {daCopy ? 'Đã copy' : 'Copy Markdown'}
            </Button>
            <Tooltip title="Mở bản gốc ở tab mới">
              <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer"><OpenInNewRoundedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Đóng">
              <IconButton onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        {dangTai ? (
          <Box sx={{ p: 5, textAlign: 'center', flex: 1 }}><CircularProgress /></Box>
        ) : loi ? (
          <Box sx={{ p: 2, flex: 1 }}>
            <Alert severity="error">
              Không tải được tài liệu: {loi}. Nguồn: <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
            </Alert>
          </Box>
        ) : (
          <>
            {mucLuc.length ? (
              <Box sx={{ width: 240, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 1, overflowY: 'auto', display: { xs: 'none', md: 'block' } }}>
                <TextField size="small" fullWidth placeholder="Lọc mục…" value={loc} onChange={(e) => setLoc(e.target.value)} sx={{ mb: 1 }} />
                {mucLoc.map((x) => (
                  <Typography key={x.id} variant="body2" onClick={() => denMuc(x.id)}
                    sx={{ py: 0.5, px: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                    {x.ten}
                  </Typography>
                ))}
              </Box>
            ) : null}
            <Box ref={vungNoiDung} sx={{ ...mdSx, flex: 1, minWidth: 0, overflowY: 'auto', px: 3, py: 1 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ h2: tieuDe('h2'), h3: tieuDe('h3') }}>{text || ''}</ReactMarkdown>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HuongDanFlow;
